import * as React from 'react';
import toast from 'react-hot-toast';

import { sendChatMessage } from '../api/services/chat';
import type { ChatMessage, ChatRequestPayload } from '../api/services/chat';
import { useInvalidateUsageQueries } from '../api/hooks/settings.hook';

export interface UseChatSessionOptions {
  storageKey?: string;
  maxMessages?: number;
}

export interface UseChatSessionResult {
  messages: ChatMessage[];
  isSending: boolean;
  error: string | null;
  sendMessage: (content: string) => Promise<void>;
  clearConversation: () => void;
}

const loadStoredMessages = (key: string): ChatMessage[] => {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as ChatMessage[];
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((item) => item && typeof item.content === 'string');
  } catch (error) {
    console.warn('Failed to load stored chat messages', error);
    return [];
  }
};

const persistMessages = (key: string, messages: ChatMessage[]) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(key, JSON.stringify(messages));
  } catch (error) {
    console.warn('Failed to persist chat messages', error);
  }
};

const buildPayload = (
  message: string,
  conversation: ChatMessage[],
  maxMessages: number,
): ChatRequestPayload => ({
  message,
  conversation: conversation.slice(-maxMessages),
});

export const useChatSession = ({
  storageKey = 'curious.chat.history',
  maxMessages = 12,
}: UseChatSessionOptions = {}): UseChatSessionResult => {
  const [messages, setMessages] = React.useState<ChatMessage[]>(() =>
    loadStoredMessages(storageKey),
  );
  const [isSending, setIsSending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const invalidateUsageQueries = useInvalidateUsageQueries();

  React.useEffect(() => {
    persistMessages(storageKey, messages);
  }, [messages, storageKey]);

  const clearConversation = React.useCallback(() => {
    setMessages([]);
    setError(null);
    persistMessages(storageKey, []);
  }, [storageKey]);

  const sendMessage = React.useCallback(
    async (content: string) => {
      const trimmed = content.trim();
      if (!trimmed || isSending) {
        return;
      }

      const timestamp = new Date().toISOString();
      const userMessage: ChatMessage = {
        role: 'user',
        content: trimmed,
        timestamp,
      };

      let optimisticConversation: ChatMessage[] = [];
      setMessages((previous) => {
        optimisticConversation = [...previous, userMessage];
        return optimisticConversation;
      });

      setIsSending(true);
      setError(null);

      const toastId = toast.loading('Sending message…');
      try {
        const response = await sendChatMessage(
          buildPayload(trimmed, optimisticConversation, maxMessages),
        );
        const assistantMessage: ChatMessage = {
          role: 'assistant',
          content: response.answer,
          citations: response.citations,
          timestamp: new Date().toISOString(),
        };
        setMessages((previous) => [...previous, assistantMessage]);
        toast.success('Reply received', { id: toastId });
        // Invalidate usage queries to refresh the timeline after receiving a response
        invalidateUsageQueries();
      } catch (err) {
        console.error('Failed to send chat message', err);
        toast.error('Unable to reach the chat service.', { id: toastId });
        setError('Unable to reach the chat service. Please try again.');
        setMessages((previous) => previous.filter((message) => message !== userMessage));
      } finally {
        setIsSending(false);
      }
    },
    [isSending, maxMessages],
  );

  return {
    messages,
    isSending,
    error,
    sendMessage,
    clearConversation,
  };
};
